import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { HITLRenderer } from '@/components/hitl';
import { cn } from '@/lib/utils';
import type { ExecutionChatBlockedStep } from '@shared/types/execution-chat';

interface ToolActionRequiredSheetProps {
  toolTitle: string;
  blockedStep: ExecutionChatBlockedStep;
  isSubmitting: boolean;
  error?: string | null;
  onSubmitAnswer: (answer: unknown) => Promise<void>;
  onCancelExecution: () => Promise<void>;
  onOpenDetails?: () => void;
  onDismiss?: () => void;
}

const toActionHeadline = (blockedStep: ExecutionChatBlockedStep): string => {
  const hitl = blockedStep.hitl;
  if (typeof hitl?.message === 'string' && hitl.message.trim().length > 0) return hitl.message.trim();
  if (typeof hitl?.formTitle === 'string' && hitl.formTitle.trim().length > 0) return hitl.formTitle.trim();
  if (typeof hitl?.intentLabel === 'string' && hitl.intentLabel.trim().length > 0) return hitl.intentLabel.trim();
  if (typeof blockedStep.error?.message === 'string' && blockedStep.error.message.trim().length > 0) return blockedStep.error.message.trim();
  return 'Domino a besoin de votre réponse pour continuer cette tâche.';
};

const toActionDescription = (blockedStep: ExecutionChatBlockedStep): string => {
  const hitl = blockedStep.hitl;
  if (typeof hitl?.formDescription === 'string' && hitl.formDescription.trim().length > 0) return hitl.formDescription.trim();
  if (typeof hitl?.context === 'string' && hitl.context.trim().length > 0) return hitl.context.trim();
  if (blockedStep.reason === 'retryable_error') {
    return 'Une reprise est possible, mais Domino a besoin d’une décision avant de relancer l’étape.';
  }
  return 'Répondez directement ici pour débloquer l’exécution sans quitter la page outil.';
};

const isTechnicalFieldKey = (value: string): boolean => (
  value.startsWith('_') || /\.\d+(\.|$)/.test(value)
);

const shouldSuppressTechnicalForm = (blockedStep: ExecutionChatBlockedStep): boolean => {
  const hitl = blockedStep.hitl;
  if (!hitl || hitl.kind !== 'form') return false;

  const fieldKeys = new Set<string>([
    ...Object.keys(hitl.formSchema ?? {}),
    ...Object.keys(hitl.formData ?? {}),
    ...Object.keys(hitl.formValidation?.fieldErrors ?? {}),
  ]);

  if (fieldKeys.size === 0) return false;

  const hasOnlyInternalKeys = [...fieldKeys].every((key) => isTechnicalFieldKey(key));
  if (!hasOnlyInternalKeys) return false;

  const labels = Object.values(hitl.formSchema ?? {})
    .map((field) => field.label?.trim() ?? '')
    .filter((label) => label.length > 0);

  return labels.every((label) => /^\d+$/.test(label));
};

export const ToolActionRequiredSheet = ({
  blockedStep,
  isSubmitting,
  error = null,
  onSubmitAnswer,
  onCancelExecution,
  onOpenDetails,
  onDismiss,
}: ToolActionRequiredSheetProps) => {
  const headline = toActionHeadline(blockedStep);
  const description = toActionDescription(blockedStep);
  const renderTechnicalFallback = shouldSuppressTechnicalForm(blockedStep);

  return (
    <div className="overflow-hidden rounded-[28px] border border-amber-200 bg-[linear-gradient(180deg,#FDFBF7_0%,#FFF4DF_100%)] shadow-[0_20px_60px_rgba(59,23,63,0.08)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-amber-100 px-5 py-5 lg:px-6">
        <div className="min-w-0">
          <h2 className="max-w-4xl text-3xl font-semibold tracking-tight text-domino-ink">
            {headline}
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-domino-inkSoft">
            {description}
          </p>
          {blockedStep.reason === 'retryable_error' && (
            <div className="mt-4 flex items-center gap-2 text-xs text-domino-inkSoft">
              <AlertTriangle className="h-3.5 w-3.5 text-domino-coral" />
              Une reprise est possible après votre réponse.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onOpenDetails && (
            <Button variant="ghost" onClick={onOpenDetails}>
              Voir les détails
            </Button>
          )}
          {onDismiss && (
            <Button variant="ghost" onClick={onDismiss} aria-label="Masquer le panneau d action requise">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-5 px-5 py-5 lg:px-6">
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {blockedStep.hitl && !renderTechnicalFallback ? (
          <div className="rounded-[24px] border border-domino-cardBorder bg-domino-warm p-4 shadow-[var(--domino-shadow-soft)]">
            <HITLRenderer
              response={blockedStep.hitl}
              isSubmitting={isSubmitting}
              onSubmit={async (answer) => {
                await onSubmitAnswer(answer);
              }}
              onCancel={async () => {
                await onCancelExecution();
              }}
            />
          </div>
        ) : (
          <div className="rounded-[24px] border border-dashed border-amber-200 bg-domino-white px-5 py-5">
            <p className="text-sm leading-6 text-domino-ink">
              {renderTechnicalFallback
                ? 'Domino n’a pas assez de données exploitables pour construire un formulaire utile ici. Relancez le tool ou ouvrez la timeline complète pour diagnostiquer le blocage.'
                : 'Cette étape attend une confirmation, mais aucun formulaire exploitable n’a été fourni.'}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-amber-100 pt-2">
          <p className="text-xs leading-5 text-domino-inkSoft">
            Le dernier résultat terminé reste consultable plus bas pendant que cette tâche attend votre décision.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void onCancelExecution()}
              disabled={isSubmitting}
            >
              Annuler cette tâche
            </Button>
            {onOpenDetails && (
              <Button variant="ghost" onClick={onOpenDetails} className={cn('gap-1')}>
                Ouvrir la timeline complète
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
